// ── <SendCopyModal> — mint one forecast COPY link ──
//
// Pure props (components/ convention): every network call lives in
// hooks/useCopyLinks.js and reaches this component as `minted` / `error` /
// `minting` / `availability` plus the `onMint` handler. No store reads, no fetch.
// The two form fields (expiry, chat opt-in) are LOCAL useState rather than
// caller-owned, so wiring this into AppInner costs a render line and not a state
// cluster — `initialExpiryDays` / `initialIncludeChat` exist so a test (or a
// caller) can seed them, since a static-markup render cannot type into an input.
//
// FIVE THINGS THIS COMPONENT MUST GET RIGHT. Each is load-bearing on honesty or
// on not destroying a capability; the capsule
// (docs/capsules/forecast-copy-links.md) is the long form.
//
// 1. `minted.url` IS THE ONLY PLACE THE SECRET EVER EXISTS. The server stored a
//    hash (server/handlers/copyLinks.js:304,333) so nothing can re-derive it. If
//    this component fails to surface it, or hides it on a later error, the owner
//    holds a live capability they cannot see and therefore cannot revoke, for up
//    to 365 days. Hence: the minted block renders whenever `minted` is non-null,
//    unconditionally, and only `onClearMinted` — an explicit user action — removes
//    it. `error` never suppresses it.
//
// 2. `minted` NON-NULL TOGETHER WITH `error` NON-NULL IS A NORMAL STATE, NOT A
//    FAILURE. The hook parks `minted` before its follow-up list read and does not
//    clear it when a later call fails (useCopyLinks.js:269-276), so "the link was
//    created; the list may be stale" is reachable on any successful mint. Rendering
//    that as an error would tell the owner their link does not exist while it is
//    live — so the error region re-words itself when `minted` is present.
//
// 3. A COPY CANNOT BE TAKEN BACK, and the owner is told so BEFORE they act (A3).
//    Revocation stops NEW redemptions only; a copy already materialised is the
//    other person's permanently. The warning is therefore the first thing in the
//    panel — a warning that appears only next to the minted URL is worthless,
//    because by then the irreversible thing has happened. The unit test pins its
//    position, not merely its presence.
//
// 4. NO RECIPIENT IDENTITY, ANYWHERE (A4). A copy link is a bearer capability:
//    there is no "send to <person>" to collect and no "who holds this" to show —
//    §10/T3 make that structurally unavailable, so an affordance implying it
//    cannot be built honestly.
//
// 5. THE COPY VERB, NEVER "SHARE" (A2). Sharing widens an ACL on the owner's own
//    forecast; copying hands over a separate forecast the owner cannot reach. The
//    string "shar" must not appear in anything this file renders.
//
// Expiry is pre-validated with `isValidExpiryDays` — a literal mirror of mint's
// own range guard — so an out-of-range value is refused here instead of costing a
// round trip and a 400. `includeChat` is a real boolean from a real checkbox: the
// server 400s a non-boolean (copyLinks.js:192-193), and the hook omits anything
// that is not `typeof === "boolean"`.

import React from "react";
import { C } from "../lib/colors.js";
import { ModalShell, CloseButton } from "./Panels.jsx";
import { copyToClipboard } from "../lib/util/clipboard.js";
import {
  DEFAULT_EXPIRY_DAYS,
  MAX_EXPIRY_DAYS,
  expiryText,
  isValidExpiryDays,
} from "../lib/copyLinks/state.js";
import { ui } from "../lib/telemetry/instrument.js";

/**
 * A3, verbatim in the markup so the test pins the promise and not a paraphrase.
 * "Revoke" is named as what it is — a stop on NEW copies — because the owner will
 * otherwise read the button as recall.
 */
export const NOT_RECALLABLE_WARNING =
  "A copy cannot be taken back. Revoking a link only stops new copies from being " +
  "made — anyone who already used the link keeps their own copy of this forecast " +
  "for good, and you cannot edit or delete it.";

/** Shown beside a freshly minted URL. See booby-trap #1 above. */
/**
 * What to tell the owner about getting this URL back.
 *
 * This string used to read "This is the only time this link is shown — the server
 * keeps only a hash of it. Save it now; it cannot be shown again." Every clause of
 * that is now false: `forecast_copy_links.secret` holds the token and
 * `GET /api/copy-links` returns the URL to its owner, which is the whole point of
 * this feature. Left as it was, it would have frightened people into treating a
 * recoverable link as a one-shot secret.
 *
 * It still says "copy" and never "share" (A2), and it does not overclaim: the URL
 * comes back only while the link is LIVE — revoke it or let it expire and the panel
 * stops offering it, which is the same rule the panel itself follows.
 */
export const SECRET_SHOWN_ONCE =
  "Copy this link now if you need it — you can also copy it again later from the " +
  "Question panel, for as long as the link stays active.";

// Running text uses the app font (IBM Plex Sans); mono is kept for figures and IDs only.
const MONO = "inherit";

/** Small sentence-case field label. */
function Label({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: C.grayL,
        marginBottom: 6,
        fontWeight: 500,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Mint one copy link for one forecast.
 *
 * @param {object} props
 * @param {boolean} [props.open]  render nothing when false. Defaults to true so a
 *   caller may instead gate with `{copyOpen && <SendCopyModal …/>}`, which is how
 *   every other modal in this tree is mounted (AccountModal, BugReportModal).
 * @param {() => void} props.onClose
 * @param {{copyId: string, url: string, expiresAt?: string}|null} [props.minted]
 *   the mint response. Its `url` carries the secret in the fragment.
 * @param {string|null} [props.error]  a STRING, never an object — `useCopyLinks`
 *   flattens `{message,kind,status}` to `error`/`errorKind`/`errorStatus`
 *   deliberately, because rendering an object as a React child throws.
 * @param {string|null} [props.errorKind]
 * @param {boolean} [props.minting]  a mint is in flight; `mint()` is single-flight
 *   and a concurrent call returns null, so this is what the button disables on.
 * @param {"unknown"|"available"|"unavailable"|"dependencyUnavailable"} [props.availability]
 * @param {(opts: {expiryDays: number, includeChat: boolean}) => unknown} props.onMint
 * @param {(() => void)|null} [props.onClearMinted]  omit to hide the dismiss button.
 * @param {unknown} [props.initialExpiryDays]
 * @param {boolean} [props.initialIncludeChat]
 * @param {number} [props.now]  epoch ms, injected so the expiry text stays clock-free.
 */
export default function SendCopyModal({
  open = true,
  onClose,
  minted = null,
  error = null,
  errorKind = null,
  minting = false,
  availability = "unknown",
  onMint,
  onClearMinted = null,
  initialExpiryDays = DEFAULT_EXPIRY_DAYS,
  initialIncludeChat = false,
  now,
}) {
  const [expiryDays, setExpiryDays] = React.useState(String(initialExpiryDays));
  const [includeChat, setIncludeChat] = React.useState(!!initialIncludeChat);

  if (!open) return null;

  const expiryValid = isValidExpiryDays(expiryDays);
  // The feature is switched off, or something it depends on is not there: minting
  // can only fail, so don't offer it. "unknown" (the hook's pre-list value) is NOT
  // in this set — treating an unanswered list read as absence would disable a
  // working surface.
  const unusable = availability === "unavailable" || availability === "dependencyUnavailable";
  const canMint = !minting && expiryValid && !unusable;

  const submit = () => {
    if (!canMint) return;
    // A number, not the input's string: harmless either way (the server coerces),
    // but the hook forwards this value verbatim and a numeric wire field should
    // be numeric.
    onMint({ expiryDays: Number(expiryDays), includeChat });
  };

  return (
    <ModalShell
      onClose={onClose}
      surface="copy"
      zIndex={600}
      panelProps={{ "data-telemetry-redact": "" }}
      panelStyle={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "24px",
        width: 520,
        maxWidth: "92vw",
        maxHeight: "85vh",
        overflowY: "auto",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <CloseButton
        onClick={onClose}
        dataPopupCancel
        ariaLabel="Close"
        style={{ position: "absolute", top: 10, right: 12, fontSize: 16, color: C.gray }}
      />
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: C.white,
          marginBottom: 16,
        }}
      >
        Create a copy link
      </div>

      {/* A3 — FIRST in the markup, before any control that mints. See #3 above. */}
      <div
        data-testid="send-copy-warning"
        style={{
          border: `1px solid ${C.orange}`,
          borderRadius: 8,
          background: C.sectionBg,
          color: C.white,
          fontFamily: MONO,
          fontSize: 12,
          lineHeight: 1.5,
          padding: "10px 12px",
          marginBottom: 16,
        }}
      >
        {NOT_RECALLABLE_WARNING}
      </div>

      <div
        style={{
          fontFamily: MONO,
          fontSize: 12,
          lineHeight: 1.5,
          color: C.grayL,
          marginBottom: 16,
        }}
      >
        Anyone holding the link below gets their own copy of this forecast. It stays live-linked:
        your updates and the eventual resolution reach their copy too.
      </div>

      <div style={{ marginBottom: 14 }}>
        <Label>Link expires after (days)</Label>
        <input
          type="number"
          min={1}
          max={MAX_EXPIRY_DAYS}
          value={expiryDays}
          aria-label="Link expires after, in days"
          data-testid="send-copy-expiry"
          {...ui("copy.expiry")}
          onChange={(e) => setExpiryDays(e.target.value)}
          style={{
            width: 120,
            background: C.inputBg,
            border: `1px solid ${expiryValid ? C.border : C.red}`,
            borderRadius: 8,
            color: C.white,
            fontFamily: MONO,
            fontSize: 12,
            padding: "8px 10px",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {!expiryValid && (
          <div
            data-testid="send-copy-expiry-invalid"
            style={{ color: C.red, fontFamily: MONO, fontSize: 12, marginTop: 6 }}
          >
            Enter a whole number of days between 1 and {MAX_EXPIRY_DAYS}.
          </div>
        )}
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          fontFamily: MONO,
          fontSize: 12,
          lineHeight: 1.5,
          color: C.grayL,
          marginBottom: 18,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={includeChat}
          data-testid="send-copy-include-chat"
          {...ui("copy.include-chat")}
          onChange={(e) => setIncludeChat(e.target.checked)}
        />
        <span>
          Include the conversation as it is right now. A snapshot — later messages do not reach the
          copy, and the copy&rsquo;s own thread is theirs to change.
        </span>
      </label>

      <button
        type="button"
        data-testid="send-copy-submit"
        {...ui("copy.create")}
        onClick={submit}
        disabled={!canMint}
        style={{
          background: canMint ? C.action : C.btnBg,
          color: canMint ? C.bg : C.gray,
          border: "none",
          borderRadius: 8,
          padding: "10px 16px",
          fontFamily: MONO,
          fontSize: 12,
          letterSpacing: 0,
          fontWeight: "bold",
          cursor: canMint ? "pointer" : "not-allowed",
        }}
      >
        {minting ? "Creating…" : "Create copy link"}
      </button>

      {/*
        The minted URL. Rendered whenever `minted` is non-null — never gated on
        `error`, per #1/#2 above. `minted` carries no `state` field, so the live
        state is supplied here: `expiryText` answers "Unknown" for a state it does
        not recognise (lib/copyLinks/state.js:127), and a link the server just
        minted is live by construction.
      */}
      {minted && (
        <div
          data-testid="send-copy-minted"
          style={{
            marginTop: 18,
            border: `1px solid ${C.green}`,
            borderRadius: 8,
            background: C.sectionBg,
            padding: "12px 14px",
          }}
        >
          <Label>Copy link created</Label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <input
              readOnly
              value={minted.url}
              aria-label="Copy link URL"
              data-testid="send-copy-url"
              {...ui("copy.url")}
              onFocus={(e) => e.target.select()}
              style={{
                flex: 1,
                minWidth: 0,
                background: C.inputBg,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.white,
                fontFamily: MONO,
                fontSize: 12,
                padding: "8px 10px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <button
              type="button"
              data-testid="send-copy-copy-btn"
              {...ui("copy.copy-url")}
              aria-label="Copy link to clipboard"
              onClick={() => copyToClipboard(minted.url)}
              style={{
                background: C.action,
                color: C.bg,
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                fontFamily: MONO,
                fontSize: 12,
                letterSpacing: 0,
                fontWeight: "bold",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              Copy link
            </button>
          </div>
          <div
            data-testid="send-copy-shown-once"
            style={{
              color: C.orange,
              fontFamily: MONO,
              fontSize: 12,
              lineHeight: 1.5,
              marginTop: 8,
            }}
          >
            {SECRET_SHOWN_ONCE}
          </div>
          <div
            data-testid="send-copy-expiry-text"
            style={{ color: C.grayL, fontFamily: MONO, fontSize: 12, marginTop: 6 }}
          >
            {expiryText({ state: "live", expiresAt: minted.expiresAt }, now)}
          </div>
          {onClearMinted && (
            <button
              type="button"
              data-testid="send-copy-dismiss"
              {...ui("copy.dismiss-link")}
              aria-label="Stop showing this copy link"
              onClick={onClearMinted}
              style={{
                marginTop: 10,
                background: C.btnBg,
                color: C.grayL,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "7px 12px",
                fontFamily: MONO,
                fontSize: 11,
                letterSpacing: 0,
                cursor: "pointer",
              }}
            >
              I&rsquo;ve saved this link
            </button>
          )}
        </div>
      )}

      {/*
        The error region, re-worded when a link already exists (#2). Both headings
        are literal strings so a test can tell the two framings apart — the failure
        wording appearing over a live link is the bug this guards.
      */}
      {error && (
        <div
          data-testid="send-copy-error"
          data-error-kind={errorKind || ""}
          style={{
            marginTop: 14,
            border: `1px solid ${minted ? C.orange : C.red}`,
            borderRadius: 8,
            background: C.sectionBg,
            padding: "10px 12px",
            fontFamily: MONO,
            fontSize: 12,
            lineHeight: 1.5,
            color: C.white,
          }}
        >
          <div
            style={{
              color: minted ? C.orange : C.red,
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {minted
              ? "Copy link created, but something else went wrong"
              : "Couldn't create the copy link"}
          </div>
          {minted && (
            <div style={{ marginBottom: 4 }}>
              The link above was created and is live. A later step failed, so the list of links you
              have made may be out of date.
            </div>
          )}
          <div data-testid="send-copy-error-message">{error}</div>
        </div>
      )}
    </ModalShell>
  );
}
